import type { TagProps } from '@lego/connect-components-react';
import { Tag } from '@lego/connect-components-react';
import { useTranslation } from 'react-i18next';
import type { FormFieldState } from '@/types/types';
import { cn } from '@/utils/cn';

const TONES = {
  type: { text: 'text-core-blue-1000', tag: 'primary' },
  default: { text: 'text-core-yellow-1000', tag: 'accent' },
  required: { text: 'text-core-green-1000', tag: 'positive' },
  limit: { text: 'text-core-slate-1100', tag: 'neutral' },
} satisfies Record<string, { text: string; tag: TagProps['variant'] }>;

export const FieldValidationInfo = ({
  field,
  asTags = false,
  className,
}: {
  field: FormFieldState;
  asTags?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();
  const constraints: { label: string; value: string; tone?: keyof typeof TONES }[] = [];

  if (field.type)
    constraints.push({
      label: t('formPage.variableEditor.validation.type'),
      value: field.type,
      tone: 'type',
    });
  if (field.initialValue)
    constraints.push({
      label: t('formPage.variableEditor.validation.defaultValue'),
      value: field.initialValue,
      tone: 'default',
    });
  if (field.required)
    constraints.push({
      label: t('formPage.variableEditor.validation.required'),
      value: t('formPage.variableEditor.validation.yes'),
      tone: 'required',
    });
  if (field.allowedCharacters)
    constraints.push({
      label: t('formPage.variableEditor.validation.allowedCharacters'),
      value: field.allowedCharacters,
    });
  if (field.fixedLength && field.length)
    constraints.push({
      label: t('formPage.variableEditor.validation.fixedLength'),
      value: t('formPage.variableEditor.validation.charactersCount', { count: field.length }),
    });
  else if (field.length)
    constraints.push({
      label: t('formPage.variableEditor.validation.maxLength'),
      value: t('formPage.variableEditor.validation.charactersCount', { count: field.length }),
    });
  if (field.minimumValue)
    constraints.push({
      label: t('formPage.variableEditor.validation.minValue'),
      value: field.minimumValue,
    });
  if (field.maximumValue)
    constraints.push({
      label: t('formPage.variableEditor.validation.maxValue'),
      value: field.maximumValue,
    });
  if (field.pickListValues?.length)
    constraints.push({
      label: t('formPage.variableEditor.validation.pickList'),
      value: field.pickListValues.join(', '),
    });
  if (field.inputFormat)
    constraints.push({
      label: t('formPage.variableEditor.validation.inputFormat'),
      value: field.inputFormat,
    });
  if (constraints.length === 0) return null;

  return (
    <ul
      aria-label={t('formPage.variableEditor.validation.title')}
      className={cn(
        'flex flex-wrap',
        asTags ? 'gap-100' : 'gap-x-150 gap-y-50 text-body-xs font-bold',
        className
      )}
    >
      {constraints.map(({ label, value, tone = 'limit' }) => (
        <li key={label} className={cn(!asTags && TONES[tone].text)}>
          {asTags ? (
            <Tag size="small" variant={TONES[tone].tag}>
              {label}: {value}
            </Tag>
          ) : (
            `${label}: ${value}`
          )}
        </li>
      ))}
    </ul>
  );
};
